import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getBootLocale } from '../i18n';

// Donors are fetched on-demand when the user drills into an ambassador
// from the "הצג הכל" modal. /api/public/... matches the rest of the public
// campaign endpoints used elsewhere on the page.
const API_BASE = '/api';

/** Locale-aware number formatter — keeps Hebrew commas for HE locale,
 * switches to US-style for EN. Used wherever we render amounts. */
const fmtAmount = (n: number): string =>
  Number(n).toLocaleString(getBootLocale() === 'en' ? 'en-US' : 'he-IL');

type AmbassadorDonor = { name: string; amount: number; createdAt: string };

// ─── Shared "ambassadors-public" block — used by the public campaign page
// AND by the campaign-builder preview, so what the campaign owner sees in
// the editor matches what donors see live. The inner block contents only —
// the outer block wrapper (id ref, wrapStyle, baseStyle padding) is left to
// the parent so the dynamic-height measurement and grid-positioning
// machinery there stay in charge.

export type AmbassadorItem = {
  name: string;
  slug: string;
  goal: number;
  raised: number;
  donorsCount: number;
  recentDonors: Array<{ name: string; amount: number; createdAt: string }>;
  // Slug of the ambassadorGroups entry this ambassador belongs to ('' = none).
  groupSlug?: string;
};

// A group of ambassadors fundraising for the same sub-goal. `raised` is the
// server-computed sum of the members' totals (never stored, never stale vs.
// the member cards).
export type AmbassadorGroupItem = {
  name: string;
  slug: string;
  goal: number;
  raised: number;
  donorsCount: number;
  membersCount: number;
  imageUrl?: string;
  // CSS object-fit for the group image: 'cover' (default) | 'contain' | 'fill'.
  imageFit?: string;
  description?: string;
  color?: string;
};

/** Resolve a group's image object-fit, defaulting to the crop-to-fill look. */
export const groupImageFit = (g: { imageFit?: string }): React.CSSProperties['objectFit'] =>
  (g.imageFit === 'contain' || g.imageFit === 'fill') ? g.imageFit : 'cover';

const AmbassadorCard: React.FC<{
  amb: AmbassadorItem; cardBg: string; accent: string; s: any; p: any;
  // onClick = whole-card click (used only for the donor drill-down in the modal).
  // onDonate = the explicit "donate via this ambassador" action — gated behind a
  // confirmation so a donor can't silently change who their donation is credited
  // to just by tapping a card (which caused real mis-attribution).
  // previewDonate renders the button for layout only (editor preview), inert.
  compact?: boolean; onClick?: () => void; onDonate?: () => void; previewDonate?: boolean; active?: boolean;
}> = ({ amb, cardBg, accent, s, p, compact, onClick, onDonate, previewDonate, active }) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pct = amb.goal > 0 ? Math.min(100, Math.round((amb.raised / amb.goal) * 100)) : 0;
  // Compact variant lets a card shrink to ~100-120px wide without breaking.
  const pad = compact ? 8 : 14;
  const avSize = compact ? 26 : 36;
  const gap = compact ? 6 : 10;
  const nameSize = compact ? 12 : 14;
  const metaSize = compact ? 10 : 12;
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        background: cardBg, borderRadius: 12, padding: pad,
        boxShadow: active ? `0 0 0 2px ${accent}, 0 2px 8px rgba(0,0,0,0.12)` : '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex', flexDirection: 'column', gap: compact ? 4 : 8,
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s, transform 0.15s',
      }}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.transform = 'translateY(-1px)'; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.transform = 'translateY(0)'; } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap, minWidth: 0 }}>
        <div style={{
          width: avSize, height: avSize, borderRadius: '50%', background: `${accent}22`,
          color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 'bold', fontSize: compact ? 11 : 14, flexShrink: 0,
        }}>
          {amb.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2)}
        </div>
        <div style={{ fontWeight: 700, color: s.textColor || '#111827', fontSize: nameSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{amb.name}</div>
      </div>
      {(p.showRaised !== false || p.showGoal !== false) && (
        <div style={{ fontSize: metaSize, color: '#6b7280', display: 'flex', justifyContent: 'space-between', gap: 4, flexWrap: compact ? 'wrap' : 'nowrap' }}>
          {p.showRaised !== false && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('public.ambassadors.raised')}: <b style={{ color: '#059669' }}>₪{fmtAmount(amb.raised)}</b></span>}
          {p.showGoal !== false && (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t('public.ambassadors.goal')}: {amb.goal > 0 ? `₪${fmtAmount(amb.goal)}` : '—'}
            </span>
          )}
        </div>
      )}
      {p.showProgress !== false && (
        // Always render the progress bar (even when goal=0) so every card
        // shows the same metric layout. When no goal exists we show an empty
        // bar with a "—" instead of "0%" so it's clear that no target is set.
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flex: 1, height: compact ? 4 : 6, borderRadius: 999, background: '#f3f4f6', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: accent, transition: 'width 0.4s' }} />
          </div>
          <span dir="ltr" style={{ fontSize: compact ? 10 : 11, fontWeight: 800, color: accent, minWidth: 32, textAlign: 'end' }}>
            {amb.goal > 0 ? `${pct}%` : '—'}
          </span>
        </div>
      )}
      {p.showDonorsCount !== false && (
        <div style={{ fontSize: compact ? 10 : 11, color: '#9ca3af' }}>{amb.donorsCount} {t('public.ambassadors.donors')}</div>
      )}
      {!compact && p.showDonorsList !== false && amb.recentDonors.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, borderTop: '1px solid #f3f4f6', paddingTop: 6 }}>
          {amb.recentDonors.slice(0, p.maxDonorsPerCard || 3).map((d, j) => (
            <div key={j} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6b7280' }}>
              <span>{d.name || t('public.recentDonors.anonymousDonor')}</span>
              <span style={{ color: '#059669', fontWeight: 600 }}>₪{Number(d.amount).toLocaleString('he-IL')}</span>
            </div>
          ))}
        </div>
      )}
      {onDonate && confirmOpen && createPortal(
        (() => {
          const en = getBootLocale() === 'en';
          const initials = amb.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2);
          return (
            <div
              onClick={() => setConfirmOpen(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 100000,
                background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
              }}
            >
              <div
                dir={en ? 'ltr' : 'rtl'}
                onClick={e => e.stopPropagation()}
                style={{
                  background: '#fff', borderRadius: 20, padding: '26px 24px 20px', width: '100%', maxWidth: 360,
                  boxShadow: '0 24px 70px rgba(0,0,0,0.3)', textAlign: 'center',
                  animation: 'ambPop .18s ease-out',
                }}
              >
                <style>{'@keyframes ambPop{from{opacity:0;transform:translateY(8px) scale(.97)}to{opacity:1;transform:none}}'}</style>
                <div style={{
                  width: 60, height: 60, borderRadius: '50%', margin: '0 auto 14px',
                  background: `${accent}1a`, color: accent, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontWeight: 800, fontSize: 22,
                }}>{initials}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
                  {en ? 'Donate via' : 'לתרום דרך'} {amb.name}{en ? '?' : '?'}
                </div>
                <div style={{ fontSize: 13.5, color: '#6b7280', lineHeight: 1.5, marginBottom: 20 }}>
                  {en
                    ? 'Your donation will be credited to this ambassador. 💛'
                    : 'התרומה שלך תיזקף לזכותו/ה. 💛'}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => { setConfirmOpen(false); onDonate(); }}
                    style={{
                      flex: 1, padding: '12px', borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: accent, color: '#fff', fontWeight: 800, fontSize: 14.5,
                    }}
                  >{en ? 'Yes, donate' : 'כן, תרום דרכו/ה'}</button>
                  <button
                    onClick={() => setConfirmOpen(false)}
                    style={{
                      padding: '12px 18px', borderRadius: 12, border: '1px solid #e5e7eb', cursor: 'pointer',
                      background: '#fff', color: '#6b7280', fontWeight: 700, fontSize: 14.5,
                    }}
                  >{en ? 'Cancel' : 'ביטול'}</button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body,
      )}
      {(onDonate || previewDonate) && (
        <button
          onClick={(e) => { e.stopPropagation(); if (onDonate) setConfirmOpen(true); }}
          style={{
            // marginTop:auto anchors the button to the BOTTOM of the card. Cards
            // in a row stretch to equal height, so the button lands in the same
            // place on every card regardless of how long its donor list is.
            marginTop: 'auto', padding: compact ? '4px 8px' : '6px 9px',
            borderRadius: 8, border: active ? 'none' : `1px solid ${accent}`, cursor: 'pointer',
            background: active ? accent : `${accent}12`, color: active ? '#fff' : accent,
            fontWeight: 700, fontSize: compact ? 10 : 11, width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          }}
        >
          {active
            ? (getBootLocale() === 'en' ? '✓ Selected' : '✓ נבחר')
            : `💛 ${getBootLocale() === 'en' ? 'Donate via' : 'תרום דרך'}`}
        </button>
      )}
    </div>
  );
};

// "Show more" modal — full ambassador list, then drills into a donor table
// per ambassador. Rendered through a React portal to `document.body` so
// position:fixed escapes the canvas's CSS `zoom` container on the public
// page (otherwise the modal would be clipped/scaled inside the canvas and
// appear to overlay the blocks below the ambassadors block).
const AmbassadorAllModal: React.FC<{
  list: AmbassadorItem[]; cardBg: string; accent: string; s: any; p: any;
  title: string; onClose: () => void; campaignSlug?: string;
  // "Donate via" from the search results: select the ambassador (sets ?ref)
  // then close the modal so the donor lands on the form. Lets a donor find an
  // ambassador by name and give through them without hunting the carousel.
  onCardClick?: (slug: string) => void;
  // Optional banner above the roster (the group drill-down renders the group's
  // image + progress here so the member list has its context).
  headerContent?: React.ReactNode;
}> = ({ list, cardBg, accent, s, p, title, onClose, campaignSlug, onCardClick, headerContent }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<{ name: string; slug: string } | null>(null);
  const [donors, setDonors] = useState<AmbassadorDonor[]>([]);
  const [donorsLoading, setDonorsLoading] = useState(false);
  const [donorsError, setDonorsError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Donor-facing sort inside the "show all" modal. Defaults to amount (the
  // most common "who raised the most" view); donors can re-sort by donor
  // count or name (א-ת) without touching the campaign's authored order.
  const [sortBy, setSortBy] = useState<string>('raised-desc');

  const filteredList = (() => {
    const q = search.trim().toLowerCase();
    const base = q ? list.filter(a => a.name.toLowerCase().includes(q)) : list;
    return sortAmbassadors(base, sortBy);
  })();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (selected) setSelected(null);
      else onClose();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, selected]);

  useEffect(() => {
    if (!selected || !campaignSlug) return;
    let cancelled = false;
    setDonorsLoading(true);
    setDonorsError(null);
    setDonors([]);
    axios
      .get(`${API_BASE}/public/campaigns/${campaignSlug}/ambassadors-public/${encodeURIComponent(selected.slug)}/donors`)
      .then(r => { if (!cancelled) setDonors(r.data?.donors || []); })
      .catch(() => { if (!cancelled) setDonorsError(t('public.ambassadors.loadFailed')); })
      .finally(() => { if (!cancelled) setDonorsLoading(false); });
    return () => { cancelled = true; };
  }, [selected, campaignSlug]);

  const modalP = { ...p, showRaised: true, showGoal: true, showProgress: true };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(getBootLocale() === 'en' ? 'en-US' : 'he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return ''; }
  };

  const totalRaised = donors.reduce((sum, d) => sum + (d.amount || 0), 0);

  const modal = (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16,
      }}
      onClick={onClose}
    >
      <div
        dir={getBootLocale() === 'en' ? 'ltr' : 'rtl'}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 20, maxWidth: 720, width: '100%',
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {selected && (
              <button
                onClick={() => setSelected(null)}
                aria-label={t('common.back')}
                style={{
                  width: 32, height: 32, borderRadius: '50%', border: 'none',
                  background: '#f3f4f6', color: '#374151', fontSize: 20, lineHeight: 1,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}
              >→</button>
            )}
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selected ? t('public.ambassadors.donorsOf', { name: selected.name }) : `🤝 ${title} (${list.length})`}
            </h3>
          </div>
          <button onClick={onClose} aria-label={t('common.close')} style={{
            width: 32, height: 32, borderRadius: '50%', border: 'none',
            background: '#f3f4f6', color: '#374151', fontSize: 20, lineHeight: 1,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>×</button>
        </div>
        <div style={{
          flex: 1, overflowY: 'auto', padding: 18,
          overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
        }}>
          {!selected ? (
            <>
              {headerContent}
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 0 }}>
                  <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('public.ambassadors.searchPlaceholder') + '...'}
                    aria-label={t('public.ambassadors.searchPlaceholder')}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '10px 38px 10px 14px',
                      borderRadius: 10,
                      border: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      fontSize: 14,
                      color: '#111827',
                      outline: 'none',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                    onFocus={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = '#fff'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
                  />
                  <span style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    color: '#9ca3af', fontSize: 16, pointerEvents: 'none',
                  }}>🔍</span>
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      aria-label={t('common.search')}
                      style={{
                        position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                        width: 24, height: 24, borderRadius: '50%', border: 'none',
                        background: '#e5e7eb', color: '#374151', fontSize: 14, lineHeight: 1,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >×</button>
                  )}
                </div>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  aria-label={t('public.ambassadors.sortBy')}
                  style={{
                    flex: '0 0 auto',
                    boxSizing: 'border-box',
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: '1px solid #e5e7eb',
                    background: '#f9fafb',
                    fontSize: 14,
                    color: '#111827',
                    outline: 'none',
                    cursor: 'pointer',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = '#fff'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
                >
                  <option value="raised-desc">{t('public.ambassadors.sortRaised')}</option>
                  <option value="donors-desc">{t('public.ambassadors.sortDonors')}</option>
                  <option value="name-asc">{t('public.ambassadors.sortName')}</option>
                </select>
              </div>
              {filteredList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 14 }}>
                  {t('public.ambassadors.noResults')}
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 12,
                }}>
                  {filteredList.map((amb, i) => (
                    <AmbassadorCard
                      key={`${amb.slug}-${i}`}
                      amb={amb}
                      cardBg={cardBg}
                      accent={accent}
                      s={s}
                      p={modalP}
                      onClick={() => setSelected({ name: amb.name, slug: amb.slug })}
                      onDonate={onCardClick ? () => { onCardClick(amb.slug); onClose(); } : undefined}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div>
              {donorsLoading && (
                <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 14 }}>{t('public.ambassadors.loading')}</div>
              )}
              {donorsError && (
                <div style={{ textAlign: 'center', padding: 24, color: '#dc2626', fontSize: 14 }}>{donorsError}</div>
              )}
              {!donorsLoading && !donorsError && donors.length === 0 && (
                <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 14 }}>
                  {t('public.ambassadors.noDonorsYet', { name: selected.name })}
                </div>
              )}
              {!donorsLoading && !donorsError && donors.length > 0 && (
                <>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 4px 14px', fontSize: 13, color: '#6b7280',
                  }}>
                    <span>{donors.length} {t('public.ambassadors.donors')}</span>
                    <span>{t('public.ambassadors.total')}: <b style={{ color: '#059669' }}>₪{fmtAmount(totalRaised)}</b></span>
                  </div>
                  <div style={{
                    border: '1px solid #f3f4f6', borderRadius: 12, overflow: 'hidden',
                  }}>
                    {donors.map((d, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto auto',
                          gap: 12, alignItems: 'center',
                          padding: '10px 14px',
                          borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
                          fontSize: 14,
                        }}
                      >
                        <span style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name || t('public.recentDonors.anonymousDonor')}</span>
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>{formatDate(d.createdAt)}</span>
                        <span style={{ color: '#059669', fontWeight: 700, minWidth: 80, textAlign: 'end' }}>
                          ₪{fmtAmount(d.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render outside the canvas's zoomed container so position:fixed actually
  // means "viewport" (CSS zoom on an ancestor confines it otherwise).
  return typeof document !== 'undefined' ? createPortal(modal, document.body) : null;
};

const AmbassadorCarousel: React.FC<{
  list: AmbassadorItem[]; perPage: number; cardBg: string; accent: string; s: any; p: any;
  autoRotateMs?: number; compact?: boolean;
  onCardClick?: (slug: string) => void; activeSlug?: string | null;
  showAllButton?: boolean; title?: string; campaignSlug?: string; previewDonate?: boolean;
}> = ({
  list, perPage, cardBg, accent, s, p, autoRotateMs = 5000, compact = false,
  onCardClick, activeSlug, showAllButton = false, title,
  campaignSlug, previewDonate,
}) => {
  const { t } = useTranslation();
  // Default title was 'השגרירים שלנו' — now resolved through i18n so EN donors
  // see 'Our Ambassadors' when no custom title was set.
  const titleEff = title || t('public.ambassadors.title');
  const pages = Math.max(1, Math.ceil(list.length / perPage));
  const [page, setPage] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const prevPageRef = useRef(0);

  // Transform-based carousel — no scrollLeft, no RTL scroll-math quirks.
  // The inner row is `pages × 100%` wide and is translated by a
  // (100 / pages)% step per page. When wrapping past the last page back
  // to the first (or vice versa) we briefly disable the CSS transition
  // so the row doesn't visibly rewind through every page in between —
  // it just instantly snaps to the new page, giving the "infinite
  // carousel" feel the user expects.
  const goTo = useCallback((target: number) => {
    const clamped = ((target % pages) + pages) % pages;
    const prev = prevPageRef.current;
    const isWrap =
      (prev === pages - 1 && clamped === 0 && target > prev) ||
      (prev === 0 && clamped === pages - 1 && target < prev);
    if (isWrap) {
      setAnimate(false);
      setPage(clamped);
      // Two RAFs: first lets the no-animation transform paint, second
      // re-enables the transition so the NEXT click animates normally.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimate(true));
      });
    } else {
      setAnimate(true);
      setPage(clamped);
    }
    prevPageRef.current = clamped;
  }, [pages]);

  // Auto-rotate; pauses while the user interacts.
  useEffect(() => {
    if (pages <= 1 || paused) return;
    const id = window.setTimeout(() => goTo(page + 1), autoRotateMs);
    return () => window.clearTimeout(id);
  }, [page, pages, paused, autoRotateMs, goTo]);

  if (list.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 20 }}>
        {t('public.ambassadors.noAmbassadorsYet')}
      </div>
    );
  }

  const showControls = pages > 1;
  const arrowStyle: React.CSSProperties = {
    position: 'absolute', top: 'calc(50% - 18px)', transform: 'translateY(-50%)',
    width: 36, height: 36, borderRadius: '50%', border: 'none',
    background: '#ffffff', boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
    cursor: 'pointer', fontSize: 22, color: '#374151', lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3, transition: 'transform 0.15s, background 0.15s',
  };

  // Reserve room on each side for the arrows so they sit OUTSIDE the cards
  // instead of overlapping them. 44px = 36px arrow + 8px breathing space.
  // Skip the reservation entirely if there's only one page (no arrows).
  const sidePad = showControls ? (compact ? 36 : 44) : 0;

  return (
    <div
      style={{ position: 'relative', paddingInline: sidePad }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
    >
      {/* Viewport with overflow:hidden — the inner row is `pages × 100%`
          wide and is translated by transform. dir follows the donor locale:
          HE keeps RTL so page 0 is the rightmost (legacy); EN switches to LTR
          so the carousel scrolls left-to-right naturally. */}
      <div style={{ overflow: 'hidden' }}>
        <div
          dir={getBootLocale() === 'en' ? 'ltr' : 'rtl'}
          style={{
            display: 'flex',
            width: `${pages * 100}%`,
            transform: `translateX(${page * (100 / pages)}%)`,
            transition: animate ? 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          }}
        >
          {Array.from({ length: pages }, (_, pIdx) => {
            const items = list.slice(pIdx * perPage, (pIdx + 1) * perPage);
            const slots: (AmbassadorItem | null)[] = [...items];
            while (slots.length < perPage) slots.push(null);
            return (
              <div
                key={pIdx}
                style={{
                  // Each page is exactly one viewport wide: (100 / pages)% of
                  // the row (which itself is `pages × 100%` of the viewport).
                  width: `${100 / pages}%`,
                  flexShrink: 0,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${perPage}, minmax(0, 1fr))`,
                  gap: compact ? 6 : 12,
                  padding: '2px 4px',
                  boxSizing: 'border-box',
                }}
              >
                {slots.map((a, i) => a ? (
                  <AmbassadorCard
                    key={`${pIdx}-${a.slug}-${i}`}
                    amb={a}
                    cardBg={cardBg}
                    accent={accent}
                    s={s}
                    p={p}
                    compact={compact}
                    onDonate={onCardClick ? () => onCardClick(a.slug) : undefined}
                    previewDonate={previewDonate}
                    active={activeSlug === a.slug}
                  />
                ) : (
                  <div key={`${pIdx}-empty-${i}`} />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {showControls && (
        <>
          {/* RTL-convention arrows: in Hebrew flow "next" lives to the LEFT.
              dir="ltr" prevents Unicode bidi from mirroring the chevron
              glyphs (without it, ‹ renders visually as › and vice versa
              inside the dir=rtl scroll container). */}
          {/* LTR-convention arrow mapping: chevron `‹` (left side) goes to
              the previous page, `›` (right side) advances to the next. Many
              donors have learned this from English UIs even on Hebrew sites,
              so matching the visual direction of the chevron with the
              direction of travel keeps the click intent obvious. */}
          <button
            type="button"
            aria-label="הקודם"
            dir="ltr"
            onClick={() => goTo(page - 1)}
            style={{ ...arrowStyle, left: 0 }}
          >‹</button>
          <button
            type="button"
            aria-label="הבא"
            dir="ltr"
            onClick={() => goTo(page + 1)}
            style={{ ...arrowStyle, right: 0 }}
          >›</button>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
            {Array.from({ length: pages }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`עבור לעמוד ${i + 1}`}
                style={{
                  width: i === page ? 22 : 8, height: 8, borderRadius: 999,
                  background: i === page ? accent : '#d1d5db',
                  border: 'none', cursor: 'pointer', padding: 0,
                  transition: 'width 0.3s, background 0.3s',
                }}
              />
            ))}
          </div>
        </>
      )}

      {showAllButton && list.length > perPage && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            display: 'block',
            margin: '12px auto 0',
            padding: '8px 20px',
            borderRadius: 999,
            border: `2px solid ${accent}`,
            background: 'transparent',
            color: accent,
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = `${accent}11`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          {t('public.ambassadors.showAll')} ({list.length})
        </button>
      )}

      {showAll && (
        <AmbassadorAllModal
          list={list}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={p}
          title={titleEff}
          onClose={() => setShowAll(false)}
          campaignSlug={campaignSlug}
          onCardClick={onCardClick}
        />
      )}
    </div>
  );
};

// List layout: 6 visible stacked, "הצג עוד" reveals the rest in a modal
// (with per-ambassador donor drill-down).
const AmbassadorList: React.FC<{
  list: AmbassadorItem[]; cardBg: string; accent: string; s: any; p: any;
  title: string; onCardClick?: (slug: string) => void; activeSlug?: string | null;
  campaignSlug?: string; previewDonate?: boolean;
}> = ({ list, cardBg, accent, s, p, title, onCardClick, activeSlug, campaignSlug, previewDonate }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const VISIBLE = 6;
  const visible = list.slice(0, VISIBLE);
  const remaining = list.length - VISIBLE;

  if (list.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 20 }}>
        {t('public.ambassadors.noAmbassadorsYet')}
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {visible.map((amb, i) => (
          <AmbassadorCard
            key={`${amb.slug}-${i}`}
            amb={amb}
            cardBg={cardBg}
            accent={accent}
            s={s}
            p={p}
            onDonate={onCardClick ? () => onCardClick(amb.slug) : undefined}
            previewDonate={previewDonate}
            active={activeSlug === amb.slug}
          />
        ))}
      </div>
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          style={{
            alignSelf: 'center',
            marginTop: 12,
            padding: '10px 22px',
            borderRadius: 999,
            border: `2px solid ${accent}`,
            background: 'transparent',
            color: accent,
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
            transition: 'background 0.2s, transform 0.1s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = `${accent}11`; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          {t('public.ambassadors.showMore')} ({remaining})
        </button>
      )}
      {showAll && (
        <AmbassadorAllModal
          list={list}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={p}
          title={title}
          onClose={() => setShowAll(false)}
          campaignSlug={campaignSlug}
          onCardClick={onCardClick}
        />
      )}
    </>
  );
};

// ─── Group cards (displayMode: 'groups') ────────────────────────────────────

/**
 * One group card: image header, raised/goal + progress (sum of members).
 * `scale` shrinks the WHOLE card proportionally — image height, fonts,
 * paddings — so more cards per row (or a narrower block) means genuinely
 * smaller cards, not tall cards with cramped text. 1 = design size.
 */
const GroupCard: React.FC<{
  group: AmbassadorGroupItem; cardBg: string; accent: string; s: any; p: any;
  onOpen?: () => void;
  scale?: number;
}> = ({ group, cardBg, accent, s, p, onOpen, scale = 1 }) => {
  const { t } = useTranslation();
  const en = getBootLocale() === 'en';
  const color = group.color || accent;
  const pct = group.goal > 0 ? Math.min(100, Math.round((group.raised / group.goal) * 100)) : 0;
  const k = scale;
  const px = (n: number, min = 1) => Math.max(min, Math.round(n * k));
  const fs = (n: number, min = 9) => Math.max(min, +(n * k).toFixed(1));
  return (
    <div
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } } : undefined}
      style={{
        background: cardBg, borderRadius: px(12, 8), overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex', flexDirection: 'column', minWidth: 0,
        cursor: onOpen ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s, transform 0.15s',
      }}
      onMouseEnter={onOpen ? (e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.14)'; } : undefined}
      onMouseLeave={onOpen ? (e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; } : undefined}
    >
      {group.imageUrl ? (
        // 'contain' letterboxes — a soft tint behind keeps the bars intentional.
        <img src={group.imageUrl} alt={group.name} style={{ width: '100%', height: px(96, 48), objectFit: groupImageFit(group), display: 'block', background: group.imageFit === 'contain' ? `${color}0d` : undefined }} />
      ) : (
        <div style={{
          width: '100%', height: px(72, 40), background: `${color}1a`, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: px(30, 18),
        }}>🎯</div>
      )}
      <div style={{ padding: px(12, 7), display: 'flex', flexDirection: 'column', gap: px(6, 3), flex: 1 }}>
        <div style={{ fontWeight: 800, color: s.textColor || '#111827', fontSize: fs(15, 11), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {group.name}
        </div>
        {group.description && (
          <div style={{ fontSize: fs(12, 9.5), color: '#6b7280', lineHeight: 1.4 }}>{group.description}</div>
        )}
        <div style={{ fontSize: fs(12, 9.5), color: '#9ca3af' }}>
          {group.membersCount} {en ? 'ambassadors' : 'שגרירים'}
          {p.showDonorsCount !== false && <> · {group.donorsCount} {t('public.ambassadors.donors')}</>}
        </div>
        {(p.showRaised !== false || p.showGoal !== false) && (
          <div style={{ fontSize: fs(13, 10), color: '#6b7280', display: 'flex', justifyContent: 'space-between', gap: 6, flexWrap: k < 0.8 ? 'wrap' : 'nowrap' }}>
            {p.showRaised !== false && <span style={{ whiteSpace: 'nowrap' }}>{t('public.ambassadors.raised')}: <b style={{ color: '#059669' }}>₪{fmtAmount(group.raised)}</b></span>}
            {p.showGoal !== false && <span style={{ whiteSpace: 'nowrap' }}>{t('public.ambassadors.goal')}: {group.goal > 0 ? `₪${fmtAmount(group.goal)}` : '—'}</span>}
          </div>
        )}
        {p.showProgress !== false && (
          <div style={{ display: 'flex', alignItems: 'center', gap: px(6, 4) }}>
            <div style={{ flex: 1, height: px(7, 4), borderRadius: 999, background: '#f3f4f6', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: color, transition: 'width 0.4s' }} />
            </div>
            <span dir="ltr" style={{ fontSize: fs(11, 9), fontWeight: 800, color, minWidth: px(32, 24), textAlign: 'end' }}>
              {group.goal > 0 ? `${pct}%` : '—'}
            </span>
          </div>
        )}
        {onOpen && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            style={{
              marginTop: 'auto', padding: `${px(7, 4)}px ${px(10, 6)}px`, borderRadius: px(8, 6),
              border: `1px solid ${color}`, background: `${color}12`, color,
              fontWeight: 700, fontSize: fs(12, 9.5), cursor: 'pointer', width: '100%',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {en ? 'View ambassadors' : 'צפייה בשגרירים'} ({group.membersCount})
          </button>
        )}
      </div>
    </div>
  );
};

/** Sort groups with the same block-level sortBy the ambassador list uses. */
function sortGroups(list: AmbassadorGroupItem[], sortBy: string | undefined): AmbassadorGroupItem[] {
  if (!sortBy || sortBy === 'manual') return list;
  const copy = [...list];
  switch (sortBy) {
    case 'raised-desc':  return copy.sort((a, b) => b.raised - a.raised);
    case 'raised-asc':   return copy.sort((a, b) => a.raised - b.raised);
    case 'name-asc':     return copy.sort((a, b) => a.name.localeCompare(b.name, 'he'));
    case 'name-desc':    return copy.sort((a, b) => b.name.localeCompare(a.name, 'he'));
    case 'donors-desc':  return copy.sort((a, b) => b.donorsCount - a.donorsCount);
    case 'goal-desc':    return copy.sort((a, b) => b.goal - a.goal);
    case 'progress-desc':return copy.sort((a, b) => {
      const pa = a.goal > 0 ? a.raised / a.goal : 0;
      const pb = b.goal > 0 ? b.raised / b.goal : 0;
      return pb - pa;
    });
    default:             return list;
  }
}

/**
 * "ראה עוד" modal — the full group list with search + sort, mirroring the
 * ambassadors' show-all modal. Clicking a group opens its member roster ON
 * TOP (the drill-down modal), so browsing groups → members → donors is one
 * continuous flow; closing the roster lands back on this list.
 */
const GroupAllModal: React.FC<{
  groups: AmbassadorGroupItem[]; cardBg: string; accent: string; s: any; p: any;
  onClose: () => void; onOpenGroup: (g: AmbassadorGroupItem) => void;
  // True while the member-roster modal is stacked on top — ESC should close
  // only the top modal, not this one with it.
  escDisabled?: boolean;
}> = ({ groups, cardBg, accent, s, p, onClose, onOpenGroup, escDisabled }) => {
  const en = getBootLocale() === 'en';
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('raised-desc');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !escDisabled) onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, escDisabled]);

  const q = search.trim().toLowerCase();
  const filtered = sortGroups(
    q ? groups.filter(g => g.name.toLowerCase().includes(q) || (g.description || '').toLowerCase().includes(q)) : groups,
    sortBy,
  );

  const inputBase: React.CSSProperties = {
    boxSizing: 'border-box', padding: '10px 14px', borderRadius: 10,
    border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: 14,
    color: '#111827', outline: 'none',
  };

  const modal = (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16,
      }}
    >
      <div
        dir={en ? 'ltr' : 'rtl'}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 20, maxWidth: 720, width: '100%',
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827' }}>
            🎯 {en ? 'All groups' : 'כל הקבוצות'} ({groups.length})
          </h3>
          <button onClick={onClose} aria-label={en ? 'Close' : 'סגירה'} style={{
            width: 32, height: 32, borderRadius: '50%', border: 'none',
            background: '#f3f4f6', color: '#374151', fontSize: 20, lineHeight: 1,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 18, overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 0 }}>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={en ? 'Search a group…' : 'חפש קבוצה…'}
                aria-label={en ? 'Search a group' : 'חיפוש קבוצה'}
                style={{ ...inputBase, width: '100%', paddingInlineEnd: 38 }}
                onFocus={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = '#fff'; }}
                onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#f9fafb'; }}
              />
              <span style={{
                position: 'absolute', insetInlineEnd: 12, top: '50%', transform: 'translateY(-50%)',
                color: '#9ca3af', fontSize: 16, pointerEvents: 'none',
              }}>🔍</span>
            </div>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              aria-label={en ? 'Sort by' : 'מיון'}
              style={{ ...inputBase, flex: '0 0 auto', cursor: 'pointer' }}
            >
              <option value="raised-desc">{en ? 'By amount raised' : 'לפי סכום שגויס'}</option>
              <option value="progress-desc">{en ? 'By progress' : 'לפי אחוז התקדמות'}</option>
              <option value="donors-desc">{en ? 'By donors' : 'לפי מספר תורמים'}</option>
              <option value="goal-desc">{en ? 'By goal' : 'לפי יעד'}</option>
              <option value="name-asc">{en ? 'By name' : 'לפי שם (א-ת)'}</option>
            </select>
          </div>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 14 }}>
              {en ? 'No groups found' : 'לא נמצאו קבוצות'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {filtered.map(g => (
                <GroupCard
                  key={g.slug}
                  group={g}
                  cardBg={cardBg}
                  accent={accent}
                  s={s}
                  p={p}
                  onOpen={() => onOpenGroup(g)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modal, document.body) : null;
};

/**
 * Groups view — group cards; clicking one drills into that group's member
 * ambassadors (reusing the roster modal, so search / sort / "donate via" /
 * per-ambassador donors all keep working inside the group).
 */
const AmbassadorGroupsView: React.FC<{
  groups: AmbassadorGroupItem[]; list: AmbassadorItem[];
  cardBg: string; accent: string; s: any; p: any;
  onCardClick?: (slug: string) => void; campaignSlug?: string;
  // Cards per carousel page (the block's "כרטיסים בשורה"; 0/'auto' → 3).
  // Card size AND typography scale with the real card width, so more cards
  // per row — or a narrower block — means genuinely smaller cards.
  columns?: number;
}> = ({ groups, list, cardBg, accent, s, p, onCardClick, campaignSlug, columns = 0 }) => {
  const { t } = useTranslation();
  const en = getBootLocale() === 'en';
  const [openGroup, setOpenGroup] = useState<AmbassadorGroupItem | null>(null);
  // "ראה עוד" — the searchable/sortable all-groups modal.
  const [showAll, setShowAll] = useState(false);

  // Ungrouped ambassadors surface as a trailing "כללי" pseudo-group unless the
  // owner turned that off — hiding people silently is worse than a plain card.
  const ungrouped = list.filter(a => !a.groupSlug);
  const displayGroups: AmbassadorGroupItem[] = sortGroups(groups, p.sortBy);
  const withGeneral = (p.showUngrouped !== false && ungrouped.length > 0)
    ? [...displayGroups, {
        name: en ? 'General' : 'כללי', slug: '__ungrouped__', goal: 0,
        raised: ungrouped.reduce((sum, a) => sum + a.raised, 0),
        donorsCount: ungrouped.reduce((sum, a) => sum + a.donorsCount, 0),
        membersCount: ungrouped.length,
      }]
    : displayGroups;

  // Carousel state — same transform-based pagination as AmbassadorCarousel
  // (RTL-safe, wrap-around snap), auto-advancing every few seconds.
  const [page, setPage] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);
  const prevPageRef = useRef(0);

  // Measure the viewport to derive the per-card scale: image height, fonts
  // and paddings all shrink together as cards get narrower. ~235px = design
  // width (scale 1); floor keeps tiny cards readable. Measured after every
  // render (React bails out when the value is unchanged) so the builder's
  // live block-resize re-scales the cards even where ResizeObserver is
  // unavailable; window-resize + RO cover out-of-render size changes.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [wrapW, setWrapW] = useState(0);
  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (el && el.clientWidth > 0) setWrapW(el.clientWidth);
  }, []);
  useLayoutEffect(() => { measure(); });
  useEffect(() => {
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    const el = wrapRef.current;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, [measure]);
  const GAP = 12;
  // Requested cards-per-row, then reduced to what actually FITS: a narrow
  // block or a phone shows fewer (much smaller) cards per page — down to a
  // single card — and the carousel keeps paging. On a typical phone that
  // lands at 2-3 shrunken cards per view. ~115px is the smallest useful card.
  const requested = Math.max(1, columns || 3);
  const MIN_CARD_W = 115;
  const fitBy = wrapW > 0 ? Math.max(1, Math.floor((wrapW - 8 + GAP) / (MIN_CARD_W + GAP))) : requested;
  const perPage = Math.min(requested, fitBy);
  const pages = Math.max(1, Math.ceil(withGeneral.length / perPage));
  const cardW = wrapW > 0 ? (wrapW - 8 - GAP * (perPage - 1)) / perPage : 0;
  const scale = cardW > 0 ? Math.max(0.55, Math.min(1.15, cardW / 235)) : 1;

  const goTo = useCallback((target: number) => {
    const clamped = ((target % pages) + pages) % pages;
    const prev = prevPageRef.current;
    const isWrap =
      (prev === pages - 1 && clamped === 0 && target > prev) ||
      (prev === 0 && clamped === pages - 1 && target < prev);
    if (isWrap) {
      setAnimate(false);
      setPage(clamped);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimate(true));
      });
    } else {
      setAnimate(true);
      setPage(clamped);
    }
    prevPageRef.current = clamped;
  }, [pages]);

  // Auto-rotate; pauses on hover/touch and while the drill-down modal is open.
  useEffect(() => {
    if (pages <= 1 || paused || openGroup) return;
    const id = window.setTimeout(() => goTo(page + 1), 5000);
    return () => window.clearTimeout(id);
  }, [page, pages, paused, openGroup, goTo]);

  // If the group list shrinks (or perPage grows) past the current page, snap back.
  useEffect(() => {
    if (page > pages - 1) { setPage(0); prevPageRef.current = 0; }
  }, [pages, page]);

  if (withGeneral.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 20 }}>
        {t('public.ambassadors.noAmbassadorsYet')}
      </div>
    );
  }

  const members = openGroup
    ? list.filter(a => openGroup.slug === '__ungrouped__' ? !a.groupSlug : a.groupSlug === openGroup.slug)
    : [];
  const openPct = openGroup && openGroup.goal > 0 ? Math.min(100, Math.round((openGroup.raised / openGroup.goal) * 100)) : 0;
  const openColor = openGroup?.color || accent;

  const showControls = pages > 1;
  const arrowStyle: React.CSSProperties = {
    position: 'absolute', top: 'calc(50% - 18px)', transform: 'translateY(-50%)',
    width: 36, height: 36, borderRadius: '50%', border: 'none',
    background: '#ffffff', boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
    cursor: 'pointer', fontSize: 22, color: '#374151', lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 3, transition: 'transform 0.15s, background 0.15s',
  };
  // Reserve room so the arrows sit OUTSIDE the cards (like the ambassadors
  // carousel). Skipped when there's a single page (no arrows).
  const sidePad = showControls ? 44 : 0;

  return (
    <>
      <div
        style={{ position: 'relative', paddingInline: sidePad }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
      >
        <div ref={wrapRef} style={{ overflow: 'hidden' }}>
          <div
            dir={en ? 'ltr' : 'rtl'}
            style={{
              display: 'flex',
              width: `${pages * 100}%`,
              transform: `translateX(${page * (100 / pages)}%)`,
              transition: animate ? 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
            }}
          >
            {Array.from({ length: pages }, (_, pIdx) => {
              const items = withGeneral.slice(pIdx * perPage, (pIdx + 1) * perPage);
              const slots: (AmbassadorGroupItem | null)[] = [...items];
              while (slots.length < perPage) slots.push(null);
              return (
                <div
                  key={pIdx}
                  style={{
                    width: `${100 / pages}%`,
                    flexShrink: 0,
                    display: 'grid',
                    gridTemplateColumns: `repeat(${perPage}, minmax(0, 1fr))`,
                    gap: GAP,
                    padding: '2px 4px',
                    boxSizing: 'border-box',
                  }}
                >
                  {slots.map((g, i) => g ? (
                    <GroupCard
                      key={`${pIdx}-${g.slug}`}
                      group={g}
                      cardBg={cardBg}
                      accent={accent}
                      s={s}
                      p={p}
                      scale={scale}
                      onOpen={() => setOpenGroup(g)}
                    />
                  ) : (
                    <div key={`${pIdx}-empty-${i}`} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {showControls && (
          <>
            <button
              type="button"
              aria-label="הקודם"
              dir="ltr"
              onClick={() => goTo(page - 1)}
              style={{ ...arrowStyle, left: 0 }}
            >‹</button>
            <button
              type="button"
              aria-label="הבא"
              dir="ltr"
              onClick={() => goTo(page + 1)}
              style={{ ...arrowStyle, right: 0 }}
            >›</button>

            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
              {Array.from({ length: pages }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={`עבור לעמוד ${i + 1}`}
                  style={{
                    width: i === page ? 22 : 8, height: 8, borderRadius: 999,
                    background: i === page ? accent : '#d1d5db',
                    border: 'none', cursor: 'pointer', padding: 0,
                    transition: 'width 0.3s, background 0.3s',
                  }}
                />
              ))}
            </div>
          </>
        )}

        {/* "ראה עוד" — plain text button opening the searchable all-groups
            modal. Only shown when there's more than one page to see. */}
        {withGeneral.length > perPage && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            style={{
              display: 'block', margin: '10px auto 0', padding: 4,
              background: 'none', border: 'none', cursor: 'pointer',
              color: accent, fontWeight: 700, fontSize: 13, textDecoration: 'underline',
            }}
          >
            {en ? 'See more' : 'ראה עוד'}
          </button>
        )}
      </div>
      {showAll && (
        <GroupAllModal
          groups={withGeneral}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={p}
          onClose={() => setShowAll(false)}
          onOpenGroup={g => setOpenGroup(g)}
          escDisabled={!!openGroup}
        />
      )}
      {openGroup && (
        <AmbassadorAllModal
          list={members}
          cardBg={cardBg}
          accent={openColor}
          s={s}
          p={p}
          title={openGroup.name}
          onClose={() => setOpenGroup(null)}
          campaignSlug={campaignSlug}
          onCardClick={onCardClick}
          headerContent={(
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14,
              padding: '10px 12px', borderRadius: 12, background: `${openColor}0d`,
            }}>
              {openGroup.imageUrl
                ? <img src={openGroup.imageUrl} alt={openGroup.name} style={{ width: 46, height: 46, borderRadius: 10, objectFit: groupImageFit(openGroup), background: openGroup.imageFit === 'contain' ? `${openColor}1a` : undefined, flexShrink: 0 }} />
                : <div style={{ width: 46, height: 46, borderRadius: 10, background: `${openColor}1a`, color: openColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🎯</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#374151', display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span>
                    {t('public.ambassadors.raised')}: <b style={{ color: '#059669' }}>₪{fmtAmount(openGroup.raised)}</b>
                    {openGroup.goal > 0 && <> {en ? 'of' : 'מתוך'} ₪{fmtAmount(openGroup.goal)}</>}
                  </span>
                  {openGroup.goal > 0 && <b dir="ltr" style={{ color: openColor }}>{openPct}%</b>}
                </div>
                <div style={{ height: 7, borderRadius: 999, background: '#e5e7eb', overflow: 'hidden', marginTop: 6 }}>
                  <div style={{ height: '100%', width: `${openPct}%`, background: openColor, transition: 'width 0.4s' }} />
                </div>
              </div>
            </div>
          )}
        />
      )}
    </>
  );
};

export function sortAmbassadors(list: AmbassadorItem[], sortBy: string | undefined): AmbassadorItem[] {
  if (!sortBy || sortBy === 'manual') return list;
  const copy = [...list];
  switch (sortBy) {
    case 'raised-desc':  return copy.sort((a, b) => b.raised - a.raised);
    case 'raised-asc':   return copy.sort((a, b) => a.raised - b.raised);
    case 'name-asc':     return copy.sort((a, b) => a.name.localeCompare(b.name, 'he'));
    case 'name-desc':    return copy.sort((a, b) => b.name.localeCompare(a.name, 'he'));
    case 'donors-desc':  return copy.sort((a, b) => b.donorsCount - a.donorsCount);
    case 'goal-desc':    return copy.sort((a, b) => b.goal - a.goal);
    case 'progress-desc':return copy.sort((a, b) => {
      const pa = a.goal > 0 ? a.raised / a.goal : 0;
      const pb = b.goal > 0 ? b.raised / b.goal : 0;
      return pb - pa;
    });
    default:             return list;
  }
}

// "הצטרף כשגריר" — public self-registration form, rendered as a portal modal.
// Used by the block-bottom "הצטרף כשגריר" button; the form itself carries the
// group picker. `lockGroupSlug` (from a ?joinGroup recruiting link) pre-selects
// a group and disables the picker, so a group recruiting flow can't be
// redirected mid-form. Posts to the public join endpoint and hands back a
// personal share link.
const AmbassadorJoinModal: React.FC<{
  campaignSlug?: string; accent: string; isPreview?: boolean;
  groups?: Array<{ name: string; slug: string }>;
  lockGroupSlug?: string;
  onClose: () => void;
}> = ({ campaignSlug, accent, isPreview, groups = [], lockGroupSlug = '', onClose }) => {
  const joinLock = lockGroupSlug && groups.some(g => g.slug === lockGroupSlug) ? lockGroupSlug : '';
  const [form, setForm] = useState({ name: '', phone: '', email: '', goal: '', groupSlug: joinLock, marketingConsent: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ shareUrl: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { setError('נא להזין שם'); return; }
    if (isPreview || !campaignSlug) { setError('הצטרפות זמינה בדף החי של הקמפיין'); return; }
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(`${API_BASE}/public/campaigns/${campaignSlug}/ambassadors/join`, {
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(),
        goal: form.goal ? Number(form.goal) : undefined,
        groupSlug: form.groupSlug || undefined,
        // Opt-in marketing consent (unchecked by default — תיקון 13)
        marketingConsent: form.marketingConsent || undefined,
      });
      const shareUrl = res.data?.shareUrl
        || `${window.location.origin}${window.location.pathname}?ref=${res.data?.slug}`;
      setResult({ shareUrl, name: res.data?.name || form.name.trim() });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'ההצטרפות נכשלה, נסו שוב');
    } finally { setSubmitting(false); }
  };

  const copy = () => {
    if (result) { navigator.clipboard?.writeText(result.shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 8, boxSizing: 'border-box' };
  const lockedName = joinLock ? (groups.find(g => g.slug === joinLock)?.name || '') : '';

  return createPortal(
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100001, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} dir="rtl" style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            {result ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 40 }}>🎉</div>
                <h3 style={{ margin: '8px 0', fontSize: 18, fontWeight: 800 }}>הצטרפת בהצלחה, {result.name}!</h3>
                <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>שתפו את הלינק האישי שלכם כדי לגייס תרומות:</p>
                <div style={{ ...inputStyle, direction: 'ltr', wordBreak: 'break-all', background: '#f9fafb', textAlign: 'left', marginBottom: 8 }}>{result.shareUrl}</div>
                <button type="button" onClick={copy} style={{ width: '100%', padding: 10, borderRadius: 8, border: 'none', background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>{copied ? '✓ הועתק' : 'העתק לינק'}</button>
              </div>
            ) : (
              <>
                <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 800 }}>
                  הצטרפות כשגריר{lockedName ? ` — ${lockedName}` : ''}
                </h3>
                <input style={inputStyle} placeholder="שם מלא *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                <input style={inputStyle} placeholder="טלפון" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                <input style={inputStyle} placeholder="אימייל" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                <input style={inputStyle} type="number" placeholder="יעד גיוס (₪, אופציונלי)" value={form.goal} onChange={e => setForm({ ...form, goal: e.target.value })} />
                {groups.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>לאיזו מטרה תגייסו?</div>
                    <select
                      style={{ ...inputStyle, marginBottom: 0, background: joinLock ? '#f3f4f6' : '#fff' }}
                      value={form.groupSlug}
                      disabled={!!joinLock}
                      onChange={e => setForm({ ...form, groupSlug: e.target.value })}
                    >
                      <option value="">— ללא קבוצה —</option>
                      {groups.map(g => <option key={g.slug} value={g.slug}>{g.name}</option>)}
                    </select>
                  </div>
                )}
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#6b7280', cursor: 'pointer', marginBottom: 8, lineHeight: 1.5 }}>
                  <input type="checkbox" checked={form.marketingConsent} onChange={e => setForm({ ...form, marketingConsent: e.target.checked })} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span>אני מאשר/ת קבלת עדכונים והודעות מהעמותה (אפשר להסיר את עצמך בכל עת)</span>
                </label>
                {/* חובת יידוע בעת איסוף מידע (סעיף 11 לחוק הגנת הפרטיות) */}
                <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 4px', lineHeight: 1.5 }}>
                  הפרטים יישמרו אצל העמותה לצורך ניהול פעילות השגרירים, בהתאם ל<a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af', textDecoration: 'underline' }}>מדיניות הפרטיות</a>.
                </p>
                {error && <p style={{ color: '#dc2626', fontSize: 12, margin: '4px 0' }}>{error}</p>}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" onClick={onClose} style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer' }}>ביטול</button>
                  <button type="button" onClick={submit} disabled={submitting} style={{ flex: 2, padding: 10, borderRadius: 8, border: 'none', background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.6 : 1 }}>{submitting ? 'שולח…' : 'הצטרף'}</button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
  );
};

// Block-bottom "➕ הצטרף כשגריר" trigger. A `?joinGroup=<grp-slug>` URL param
// (group recruiting link) locks the picker to that group AND opens the form
// immediately, so the shared link lands straight on the sign-up.
const AmbassadorJoinButton: React.FC<{
  campaignSlug?: string; accent: string; isPreview?: boolean;
  groups?: Array<{ name: string; slug: string }>;
}> = ({ campaignSlug, accent, isPreview, groups = [] }) => {
  const joinLock = (() => {
    try {
      const v = new URLSearchParams(window.location.search).get('joinGroup') || '';
      return groups.some(g => g.slug === v) ? v : '';
    } catch { return ''; }
  })();
  const [open, setOpen] = useState(() => !isPreview && !!joinLock);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{ alignSelf: 'center', padding: '10px 22px', borderRadius: 999, border: 'none', background: accent, color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
      >
        ➕ הצטרף כשגריר
      </button>
      {open && (
        <AmbassadorJoinModal
          campaignSlug={campaignSlug}
          accent={accent}
          isPreview={isPreview}
          groups={groups}
          lockGroupSlug={joinLock}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export const AmbassadorsPublicBlock: React.FC<{
  list: AmbassadorItem[];
  s: any;
  p: any;
  primary?: string;
  isMobile: boolean;
  onCardClick?: (slug: string) => void;
  activeSlug?: string | null;
  // When set, "הצג הכל" → modal drill-down fetches donors per ambassador
  // from /public/campaigns/:slug/ambassadors-public/:ambSlug/donors. Omitted
  // in the builder preview (no real campaign slug), so the drill-down is
  // still navigable but shows the loading/empty state for donors.
  campaignSlug?: string;
  // Render the "donate via" button even without a click handler (editor preview)
  // so the designer sees the real card height and can space the block correctly.
  previewDonate?: boolean;
  // Campaign ambassador groups with computed totals (displayMode: 'groups').
  groups?: AmbassadorGroupItem[];
}> = ({ list: rawList, s, p, primary, isMobile, onCardClick, activeSlug, campaignSlug, previewDonate, groups = [] }) => {
  const { t } = useTranslation();
  const list = sortAmbassadors(rawList, p.sortBy);
  const accent = p.accentColor || primary || '#2563eb';
  const cardBg = p.cardBgColor || '#ffffff';
  const layoutMode = p.layout || 'grid';
  // Default title — 'השגרירים שלנו' in HE / 'Our Ambassadors' in EN.
  const titleText = p.title || t('public.ambassadors.title');
  // Cards-per-row: with a fixed count the cards are `1fr` each, so their SIZE
  // follows the block's width — resizing the block in the editor directly
  // scales the cards. 'auto' (groups mode only) = pack as many ≥210px columns
  // as fit, the pre-setting behavior. Default 3, like the carousel always was.
  const cardsPerRow = p.cardsPerRow === 'auto'
    ? 0
    : Math.min(6, Math.max(1, Math.round(Number(p.cardsPerRow)) || 3));
  // mobile carousel: 1 card/page + "הצג הכל" button → modal
  // desktop carousel: cards-per-row per page (default 3) with arrows + dots
  const perPage = isMobile ? 1 : (cardsPerRow || 3);
  // Force the headline metrics on every card. Block-level toggles still
  // control the donor strip / donor count.
  const cardP = { ...p, showRaised: true, showGoal: true, showProgress: true };
  // Groups mode only kicks in when groups actually exist — a campaign whose
  // owner flipped the toggle before creating any group keeps showing the
  // regular ambassador cards instead of an empty block.
  const groupsMode = p.displayMode === 'groups' && groups.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} dir={getBootLocale() === 'en' ? 'ltr' : 'rtl'}>
      {p.title && (
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: s.textColor || '#111827', textAlign: 'center' }}>
          {p.title}
        </h3>
      )}
      {groupsMode ? (
        <AmbassadorGroupsView
          groups={groups}
          list={list}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={cardP}
          onCardClick={onCardClick}
          campaignSlug={campaignSlug}
          columns={cardsPerRow}
        />
      ) : layoutMode === 'list' ? (
        <AmbassadorList
          list={list}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={cardP}
          title={titleText}
          onCardClick={onCardClick}
          activeSlug={activeSlug}
          campaignSlug={campaignSlug}
          previewDonate={previewDonate}
        />
      ) : (
        <AmbassadorCarousel
          list={list}
          perPage={perPage}
          cardBg={cardBg}
          accent={accent}
          s={s}
          p={cardP}
          onCardClick={onCardClick}
          activeSlug={activeSlug}
          // Show the "הצג הכל" button on every viewport — even on desktop
          // where the carousel arrows exist, donors often prefer scanning
          // the full roster at once. The button hides itself automatically
          // when there's nothing to "show more" (list.length ≤ perPage).
          showAllButton
          title={titleText}
          campaignSlug={campaignSlug}
          previewDonate={previewDonate}
        />
      )}
      {p.allowSelfJoin && (
        <AmbassadorJoinButton
          campaignSlug={campaignSlug}
          accent={accent}
          isPreview={!!previewDonate}
          groups={groups.map(g => ({ name: g.name, slug: g.slug }))}
        />
      )}
    </div>
  );
};
